import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  /** 0-indexed page number — matches the API's `page` argument. */
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Singular/plural unit shown in the range label, e.g. "post". Defaults to "item". */
  unit?: string;
  className?: string;
}

/**
 * Compact pagination control: range summary on the left, prev/page-
 * numbers/next nav on the right. Handles ellipsis for >7 pages and
 * collapses to just the range text when there's only one page of
 * results. Pages are 0-indexed externally to match the hd-api
 * convention; UI labels are 1-indexed.
 */
export function Pagination({
  page,
  perPage,
  total,
  onPageChange,
  unit = "item",
  className = "",
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const start = total === 0 ? 0 : page * perPage + 1;
  const end = Math.min(total, (page + 1) * perPage);
  const plural = total === 1 ? unit : `${unit}s`;

  if (total === 0) {
    return (
      <div
        className={`flex items-center justify-between gap-3 text-[12px] text-ink-4 ${className}`}
      >
        <span>No {plural}</span>
      </div>
    );
  }

  const items = pageItems(page, totalPages);
  const showNav = totalPages > 1;

  return (
    <div
      className={`flex items-center justify-between gap-3 ${className}`}
    >
      <div className="text-[12px] text-ink-3">
        Showing <span className="num">{start}</span>–
        <span className="num">{end}</span> of{" "}
        <span className="num">{total}</span> {plural}
      </div>
      {showNav && (
        <nav
          aria-label="Pagination"
          className="flex items-center gap-0.5"
        >
          <PageButton
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <ChevronLeft size={13} strokeWidth={2} />
          </PageButton>
          {items.map((item, i) =>
            item === "ellipsis" ? (
              <span
                key={`e${i}`}
                className="num px-1.5 text-[12px] text-ink-5"
                aria-hidden
              >
                …
              </span>
            ) : (
              <PageButton
                key={item}
                onClick={() => onPageChange(item)}
                active={item === page}
                aria-label={`Page ${item + 1}`}
                aria-current={item === page ? "page" : undefined}
              >
                <span className="num">{item + 1}</span>
              </PageButton>
            ),
          )}
          <PageButton
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            aria-label="Next page"
          >
            <ChevronRight size={13} strokeWidth={2} />
          </PageButton>
        </nav>
      )}
    </div>
  );
}

interface PageButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

function PageButton({
  active,
  className = "",
  children,
  ...rest
}: PageButtonProps) {
  return (
    <button
      type="button"
      className={`flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-[12px] transition disabled:cursor-not-allowed disabled:opacity-40 ${
        active
          ? "shadow-card bg-paper-hi font-medium text-ink"
          : "text-ink-3 hover:bg-paper-2"
      } ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * Pick which page numbers to display. ≤7 pages: show all. >7 pages:
 * show first, last, current, current±1, with ellipses where there are
 * gaps. Ensures the current page is always visible without overflow.
 */
function pageItems(
  page: number,
  totalPages: number,
): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }
  const set = new Set<number>([
    0,
    totalPages - 1,
    page,
    page - 1,
    page + 1,
  ]);
  const valid = Array.from(set)
    .filter((p) => p >= 0 && p < totalPages)
    .sort((a, b) => a - b);
  const result: Array<number | "ellipsis"> = [];
  for (let i = 0; i < valid.length; i++) {
    if (i > 0 && valid[i] - valid[i - 1] > 1) result.push("ellipsis");
    result.push(valid[i]);
  }
  return result;
}
